package fr.frux.core.designsystem.zoom

import androidx.compose.animation.core.animate
import androidx.compose.animation.core.tween
import androidx.compose.foundation.gestures.awaitEachGesture
import androidx.compose.foundation.gestures.awaitFirstDown
import androidx.compose.foundation.gestures.calculateCentroid
import androidx.compose.foundation.gestures.calculateCentroidSize
import androidx.compose.foundation.gestures.calculatePan
import androidx.compose.foundation.gestures.calculateZoom
import androidx.compose.foundation.gestures.detectTapGestures
import androidx.compose.runtime.Stable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.setValue
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clipToBounds
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.geometry.Size
import androidx.compose.ui.graphics.graphicsLayer
import androidx.compose.ui.input.pointer.PointerEvent
import androidx.compose.ui.input.pointer.PointerEventType
import androidx.compose.ui.input.pointer.PointerInputChange
import androidx.compose.ui.input.pointer.PointerInputScope
import androidx.compose.ui.input.pointer.pointerInput
import androidx.compose.ui.input.pointer.positionChanged
import androidx.compose.ui.layout.onSizeChanged
import androidx.compose.ui.unit.toSize
import kotlinx.coroutines.coroutineScope
import kotlinx.coroutines.launch
import kotlin.math.abs

private const val DOUBLE_TAP_ANIMATION_MS = 250

/**
 * État de zoom d'une image (#686). Les calculs vivent dans `ZoomMath.kt`, en fonctions pures
 * testées. Cette classe tient la transformation courante et les tailles qui la bornent. [animate]
 * à false (réduction des animations demandée) rend le double tap instantané.
 */
@Stable
class ZoomState(
    private val animate: Boolean = true,
) {
    var transform by mutableStateOf(ZoomTransform.Identity)
        private set

    val isZoomed: Boolean get() = transform.isZoomed

    private var containerSize = Size.Zero
    private var intrinsicSize = Size.Unspecified

    private val contentSize: Size get() = fittedContentSize(intrinsicSize, containerSize)

    /** Taille propre de l'image chargée, pour borner le déplacement à l'image et non au cadre. */
    fun setIntrinsicSize(size: Size) {
        intrinsicSize = size
        reclamp()
    }

    fun reset() {
        transform = ZoomTransform.Identity
    }

    internal fun setContainerSize(size: Size) {
        containerSize = size
        reclamp()
    }

    internal fun applyGesture(
        zoomFactor: Float,
        centroid: Offset,
        pan: Offset,
    ) {
        transform = zoomAround(transform, zoomFactor, centroid.fromCenter(), pan, containerSize, contentSize)
    }

    internal suspend fun toggleDoubleTap(tap: Offset) {
        val target = doubleTapTarget(transform, tap.fromCenter(), containerSize, contentSize)
        if (!animate) {
            transform = target
            return
        }
        val start = transform
        animate(0f, 1f, animationSpec = tween(DOUBLE_TAP_ANIMATION_MS)) { fraction, _ ->
            transform = lerp(start, target, fraction)
        }
    }

    /** Un pincement relâché juste au-dessus de 1x revient pile à 1x, pour rendre la main au pager. */
    internal fun settle() {
        if (!transform.isZoomed) reset()
    }

    private fun reclamp() {
        transform = transform.copy(offset = clampOffset(transform.offset, transform.scale, containerSize, contentSize))
    }

    private fun Offset.fromCenter(): Offset = this - Offset(containerSize.width / 2f, containerSize.height / 2f)
}

/**
 * Zoom d'image en `commonMain`, sans dépendance (#686) : pincement de 1x à 4x, double tap
 * 1x ↔ 2,5x centré sur le point touché, molette ou trackpad sur web et desktop, déplacement borné.
 *
 * Pensé pour vivre dans un `HorizontalPager` : à 1x, un glisser à un doigt n'est jamais consommé
 * et reste au pager. Le pan n'est pris qu'une fois zoomé, et le pincement dès deux doigts.
 * L'appelant coupe en plus `userScrollEnabled` du pager tant que [ZoomState.isZoomed].
 */
fun Modifier.zoomable(state: ZoomState): Modifier =
    this
        .clipToBounds()
        .onSizeChanged { state.setContainerSize(it.toSize()) }
        .pointerInput(state) {
            coroutineScope {
                detectTapGestures(onDoubleTap = { launch { state.toggleDoubleTap(it) } })
            }
        }.pointerInput(state) { detectZoomAndPan(state) }
        .pointerInput(state) { detectWheelZoom(state) }
        .graphicsLayer {
            val transform = state.transform
            scaleX = transform.scale
            scaleY = transform.scale
            translationX = transform.offset.x
            translationY = transform.offset.y
        }

private suspend fun PointerInputScope.detectZoomAndPan(state: ZoomState) {
    awaitEachGesture {
        // Le détecteur de tap voisin consomme le down : on prend le geste quand même.
        awaitFirstDown(requireUnconsumed = false)
        val slop = TouchSlop(viewConfiguration.touchSlop)
        do {
            val event = awaitPointerEvent()
            val canceled = event.changes.any { it.isConsumed }
            // À 1x et à un doigt, le glisser appartient au pager.
            val ours = event.changes.count { it.pressed } >= 2 || state.isZoomed
            if (!canceled && ours) applyTransformEvent(event, state, slop)
        } while (!canceled && event.changes.any { it.pressed })
        state.settle()
    }
}

/** Seuil de démarrage d'un geste, cumulé sur le zoom et le pan comme `detectTransformGestures`. */
private class TouchSlop(
    private val threshold: Float,
) {
    private var zoom = 1f
    private var pan = Offset.Zero
    var passed = false
        private set

    fun accumulate(
        zoomChange: Float,
        panChange: Offset,
        centroidSize: Float,
    ) {
        if (passed) return
        zoom *= zoomChange
        pan += panChange
        passed = abs(1 - zoom) * centroidSize > threshold || pan.getDistance() > threshold
    }
}

private fun applyTransformEvent(
    event: PointerEvent,
    state: ZoomState,
    slop: TouchSlop,
) {
    val zoomChange = event.calculateZoom()
    val panChange = event.calculatePan()
    slop.accumulate(zoomChange, panChange, event.calculateCentroidSize(useCurrent = false))
    if (!slop.passed) return
    if (zoomChange != 1f || panChange != Offset.Zero) {
        state.applyGesture(zoomChange, event.calculateCentroid(useCurrent = false), panChange)
    }
    event.changes.forEach { if (it.positionChanged()) it.consume() }
}

private suspend fun PointerInputScope.detectWheelZoom(state: ZoomState) {
    awaitPointerEventScope {
        while (true) {
            val event = awaitPointerEvent()
            val change = event.changes.firstOrNull()
            if (event.type == PointerEventType.Scroll && change != null) applyWheel(change, state)
        }
    }
}

private fun applyWheel(
    change: PointerInputChange,
    state: ZoomState,
) {
    val delta = change.scrollDelta
    // Un défilement surtout horizontal (trackpad) reste au pager.
    if (delta.y == 0f || abs(delta.x) > abs(delta.y)) return
    state.applyGesture(wheelZoomFactor(delta.y, wheelDeltaPerNotch), change.position, Offset.Zero)
    state.settle()
    change.consume()
}
